import { uniq } from 'lodash-es';
import { FitnessPlan, Plan_SubSlot, Plan_Workout } from '@cf-shared/data-model/fitness-plan';
import { getIntensityForSubSlot } from '@cf-shared/workout-solver-intensity-constants';
import { fixupSlotId, isObsoleteSlotId } from 'services/slot-id-fixup';
import { gcOrphanSubslots } from './orphan-subslot-cleanup';
import { MissingSubSlotsHealed } from './orphan-subslot-reporter';

/** The dictionary of sub-slots a program holds, keyed by sub-slot id. */
type SubSlotDictionary = { [subSlotId: string]: Plan_SubSlot };

/**
 * Whether a stored program's sub-slot dictionary is something we can walk at all.
 *
 * One named test rather than the same condition written out at each step: a very old or
 * damaged document can hold anything here, every repair below has to ask, and somebody adding
 * the next step should copy a call rather than re-derive the condition and get half of it.
 */
function isReadableSubSlotDictionary(subSlots: unknown): subSlots is SubSlotDictionary {
  return !!subSlots && typeof subSlots === 'object';
}

/** What one run of {@link repairProgramFormatAtLoad} found and changed. */
export interface ProgramFormatRepairResult {
  /** whether anything was repaired, so the caller knows to save */
  changed: boolean;
  /**
   * the day entries that pointed at sub-slots the program no longer holds, ready to hand to
   * `reportMissingSubSlotsHealedAtLoad`. This module reports nothing itself — the caller
   * decides whether the program it passed is worth telling Sentry about.
   */
  healedMissing: MissingSubSlotsHealed;
}

/**
 * Rewrite obsolete slot ids: each sub-slot's own `slotId`, and the key it is stored under.
 *
 * Its own function because `UserDbService.backCompatFitnessPlan` runs it for callers that do
 * NOT get the rest of the load-time repair — the boot migration and the old-plans list — so
 * there is still exactly one definition of this step.
 *
 * Returns whether anything changed.
 */
export function repairObsoleteSubSlotIds(subSlots: SubSlotDictionary | undefined): boolean {
  if (!isReadableSubSlotDictionary(subSlots)) return false;
  let changed = false;

  for (const subSlotId of Object.keys(subSlots)) {
    const subSlot = subSlots[subSlotId];
    // a slotId that is not text is damage fixupSlotId cannot read; leave it for the rebuild
    if (typeof subSlot?.slotId === 'string' && fixupSlotId(subSlot.slotId) !== subSlot.slotId) {
      subSlot.slotId = fixupSlotId(subSlot.slotId);
      changed = true;
    }
    if (isObsoleteSlotId(subSlotId)) {
      subSlots[fixupSlotId(subSlotId)] = subSlot;
      delete subSlots[subSlotId];
      changed = true;
    }
  }
  return changed;
}

/**
 * Fill in the intensity table of any sub-slot that has none, working it out from the sub-slot
 * id the way the program build does.
 *
 * Its own function because `loadPlanAsync` runs it a second time after the #4587 recovery has
 * put exercises back into an emptied program: those restored sub-slots come from a stored copy
 * that may predate intensities, and they arrive after the repair below has already run.
 *
 * Returns whether anything changed.
 */
export function fillMissingIntensities(subSlots: SubSlotDictionary | undefined): boolean {
  if (!isReadableSubSlotDictionary(subSlots)) return false;
  let changed = false;

  for (const subSlotId of Object.keys(subSlots)) {
    const subSlot = subSlots[subSlotId];
    // a sub-slot that is not an object cannot take the field, so it is left for the rebuild
    if (subSlot && typeof subSlot === 'object' && subSlot.intensities == null) {
      subSlot.intensities = getIntensityForSubSlot(subSlotId);
      changed = true;
    }
  }
  return changed;
}

/**
 * Bring one training program into today's format: the repairs the app applies to a stored
 * program every time it is loaded.
 *
 * THIS IS THE ONE DEFINITION OF THAT SEQUENCE. Two programs get it — the live one the user
 * trains from (`UserDbService.loadPlanAsync`) and the as-built copy the rebuild warning
 * compares against (`normalizeProgramBackupAtLoad`) — and they used to be two hand-kept
 * copies of the same steps, so a repair added to the live program and forgotten on the copy
 * made the app wrongly warn that a rebuild would lose customizations nobody had made
 * (df-0c02f36b5c60, which followed df-dadb3d927571). Add a new load-time repair HERE and both
 * get it.
 *
 * Each program is repaired against ITS OWN days, never by copying what the other one did, so
 * a sub-slot a person really removed still reads as an edit. Why: see
 * `design-docs/exercise-replacement.md`, "Restoring the built program".
 *
 * It reports nothing. The caller decides that, because the same damage matters differently on
 * the live program than on a copy of it — the returned result carries what was stripped.
 *
 * Every step guards against a shape a damaged or very old document can have, and repairs what
 * it can rather than throwing: a program that cannot be read at all is not a reason to stop
 * the app loading.
 */
export function repairProgramFormatAtLoad(program: FitnessPlan): ProgramFormatRepairResult {
  const subSlots = program.subSlots;
  // counted before any repair below changes the program, so the report describes what was
  // stored rather than what is left
  const result: ProgramFormatRepairResult = {
    changed: false,
    healedMissing: {
      missingIds: [],
      workoutNames: [],
      totalWorkouts: Array.isArray(program.workouts) ? program.workouts.length : 0,
      totalSubSlots: isReadableSubSlotDictionary(subSlots) ? Object.keys(subSlots).length : 0,
    },
  };

  // old slot ids, both on the sub-slot and in the key it is filed under
  if (repairObsoleteSubSlotIds(subSlots)) {
    result.changed = true;
  }

  // the days, and the sub-slots nothing references any more
  repairDays(program, subSlots, result);

  // missing intensity tables, filled from the sub-slot id
  if (fillMissingIntensities(subSlots)) {
    result.changed = true;
  }

  return result;
}

/**
 * Repair the training days: drop a day that is not an object at all, repair each day's list of
 * exercises, and then collect the sub-slots no day references any more.
 *
 * Its own function so the repairs run at the top of a body rather than inside a wrapping `if` —
 * `repairProgramFormatAtLoad` is the place a future load-time repair is meant to be added, and
 * an extra level of indentation there is paid again by whoever adds the next one.
 *
 * Records what it stripped, and whether anything changed, into `result`.
 */
function repairDays(
  program: FitnessPlan, subSlots: SubSlotDictionary | undefined, result: ProgramFormatRepairResult,
): void {
  // a program with no days, or one whose sub-slot dictionary cannot be read, has nothing here
  // to repair against
  if (!Array.isArray(program.workouts) || !isReadableSubSlotDictionary(subSlots)) return;

  // a day that is not an object is damage the repairs below would throw on; drop it, so a
  // broken program cannot stop every load
  const days = program.workouts.filter(workout => workout && typeof workout === 'object');
  if (days.length !== program.workouts.length) {
    program.workouts = days;
    result.changed = true;
  }

  for (const workout of program.workouts) {
    repairOneDaysExerciseList(workout, subSlots, result);
  }

  // name each day once, however many of its ids were stripped
  result.healedMissing.workoutNames = uniq(result.healedMissing.workoutNames);

  // #3320: the other direction — sub-slots no day references at all. gcOrphanSubslots keeps
  // its own #4587 guard, which refuses to empty a built program whose days reference nothing
  if (gcOrphanSubslots(program).length > 0) {
    result.changed = true;
  }
}

/**
 * Repair one day's list of exercises: drop ids that are not text, map obsolete ids, drop ids
 * with no sub-slot behind them, and drop repeats (#3483 — the pre-fix add path could push an id
 * the day already referenced).
 *
 * Records the dropped ids, and whether anything changed, into `result`.
 */
function repairOneDaysExerciseList(
  workout: Plan_Workout, subSlots: SubSlotDictionary, result: ProgramFormatRepairResult,
): void {
  // a list that is there but is not an array cannot be walked; treat it as empty
  if (!Array.isArray(workout.subSlotIds)) {
    if (workout.subSlotIds != null) {
      workout.subSlotIds = [];
      result.changed = true;
    }
    return;
  }

  const repaired: string[] = [];
  for (const storedId of workout.subSlotIds) {
    // an id that is not text cannot name a sub-slot; drop it rather than look it up
    if (typeof storedId !== 'string') continue;
    const subSlotId = fixupSlotId(storedId);
    if (!subSlots[subSlotId]) {
      // the day points at a sub-slot the program no longer holds. Drop the reference and
      // hand it back, so a caller that cares can say what the load stripped out
      result.healedMissing.missingIds.push(subSlotId);
      result.healedMissing.workoutNames.push(workout.name);
      continue;
    }
    // keep the first occurrence of a repeated id, drop the rest
    if (!repaired.includes(subSlotId)) repaired.push(subSlotId);
  }

  const same = repaired.length === workout.subSlotIds.length
    && repaired.every((subSlotId, index) => subSlotId === workout.subSlotIds[index]);
  if (!same) {
    workout.subSlotIds = repaired;
    result.changed = true;
  }
}
